import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { Seo } from '../components/Seo';
import { useUserInfo, DashDevice, Site } from '../hooks/useUserInfo';
import api from '../services/api';
import { PlanPicker } from '../components/subscription/PlanPicker';
import { PasswordSettings } from '../components/dashboard/PasswordSettings';
import { TwoFactorSettings } from '../components/dashboard/TwoFactorSettings';
import { DiscordSettings } from '../components/dashboard/DiscordSettings';
import { DeviceResetPanel } from '../components/dashboard/DeviceResetPanel';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

// Stable "latest" installer URLs — the release deploy refreshes these fixed
// filenames each version, so the download always serves the current build (no
// site rebuild per release). Mirrors the landing page.
const DL = {
  win: 'https://localareacloud.com/downloads/LACloud_x64-setup.exe',
  mac: 'https://localareacloud.com/downloads/LACloud_mac.zip',
  linuxAppImage: 'https://localareacloud.com/downloads/LACloud_amd64.AppImage',
  linuxDeb: 'https://localareacloud.com/downloads/LACloud_amd64.deb',
  appStore: 'https://apps.apple.com/app/id6790067880',
  googlePlay: 'https://play.google.com/store/apps/details?id=com.lacloud.mobile',
};

type Section = 'overview' | 'devices' | 'downloads' | 'plan' | 'account';

/* ---------- inline icons (stroke) ---------- */
const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const IOverview = () => (<svg viewBox="0 0 24 24" {...s}><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /></svg>);
const IDevices = () => (<svg viewBox="0 0 24 24" {...s}><rect x="2" y="4" width="14" height="10" rx="1.5" /><path d="M2 18h14M8 14v4" /><rect x="18" y="9" width="4" height="11" rx="1" /></svg>);
const IDownload = () => (<svg viewBox="0 0 24 24" {...s}><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 19h16" /></svg>);
const IPlan = () => (<svg viewBox="0 0 24 24" {...s}><path d="M12 3l2.4 5 5.6.7-4 3.9 1 5.6L12 21l-5-2.8 1-5.6-4-3.9 5.6-.7L12 3z" /></svg>);
const IAccount = () => (<svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 3.6-6 8-6s8 2 8 6" /></svg>);
const ICheck = () => (<svg viewBox="0 0 24 24" {...s}><path d="M20 6 9 17l-5-5" /></svg>);
const IWin = () => (<svg viewBox="0 0 24 24" {...s}><path d="M3 5.5 10.5 4v7.5H3V5.5zM12 3.8 21 2.5V11.5H12V3.8zM3 12.5h7.5V20L3 18.5v-6zM12 12.5h9V21.5L12 20.2v-7.7z" /></svg>);
const IApple = () => (<svg viewBox="0 0 24 24" {...s}><path d="M15.5 8.5c-1.2 0-2 .7-2.9.7-1 0-1.9-.7-3-.7-1.7 0-3.6 1.4-3.6 4.3 0 3 2.3 6.2 3.7 6.2.9 0 1.3-.6 2.5-.6 1.2 0 1.5.6 2.5.6 1.4 0 3.3-3 3.3-4.6-2.1-.8-2.4-3.9-.4-5.1-.7-.8-1.7-1.2-2.6-1.2z" /><path d="M13.5 5.5c.6-.7 1-1.6.9-2.5-1 .1-1.8.6-2.4 1.3-.5.6-.9 1.5-.8 2.4 1 .1 1.8-.5 2.3-1.2z" /></svg>);
const ILinux = () => (<svg viewBox="0 0 24 24" {...s}><path d="M9 3.5c-1 1-1.2 2.5-1 4 .2 1.4.2 2.4-.6 3.6C6 12.8 5 14.6 5 16.4c0 1.6 1 2.1 2 2.6M15 3.5c1 1 1.2 2.5 1 4-.2 1.4-.2 2.4.6 3.6 1.4 1.7 2.4 3.5 2.4 5.3 0 1.6-1 2.1-2 2.6" /><path d="M9.5 8.2h.01M14.5 8.2h.01M9 18.5c1 1.2 2 1.5 3 1.5s2-.3 3-1.5" /></svg>);
const IMobile = () => (<svg viewBox="0 0 24 24" {...s}><rect x="6" y="2.5" width="12" height="19" rx="2.5" /><path d="M11 18.5h2" /></svg>);
const ISite = () => (<svg viewBox="0 0 24 24" {...s}><path d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11z" /><circle cx="12" cy="10" r="2.5" /></svg>);
const IRoam = () => (<svg viewBox="0 0 24 24" {...s}><rect x="7" y="2.5" width="10" height="19" rx="2.5" /><path d="M11 18.5h2" /></svg>);

/** Sentinel for the site <select>: a device that travels between sites rather
 *  than living at one. Not a real site id — it maps to the roaming endpoint. */
const ROAMING = '__roaming__';

/** SQLite gives us 0/1; older payloads may omit it entirely. */
const isRoaming = (d: DashDevice): boolean => !!d.is_roaming;

const NAV: { key: Section; label: string; icon: React.FC }[] = [
  { key: 'overview', label: 'Overview', icon: IOverview },
  { key: 'devices', label: 'Devices', icon: IDevices },
  { key: 'downloads', label: 'Downloads', icon: IDownload },
  { key: 'plan', label: 'Plan & billing', icon: IPlan },
  { key: 'account', label: 'Account', icon: IAccount },
];

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null;

const fmtWhen = (d?: string) => {
  if (!d) return 'Unknown';
  const then = new Date(d).getTime();
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const platformIcon = (p?: string): React.FC => {
  const k = (p || '').toLowerCase();
  if (k.includes('win')) return IWin;
  if (k.includes('mac') || k.includes('darwin') || k.includes('ios')) return IApple;
  if (k.includes('linux')) return ILinux;
  if (k.includes('android') || k.includes('phone') || k.includes('mobile')) return IMobile;
  return IDevices;
};

export const Dashboard: React.FC = () => {
  const { user, loading } = useAuth();
  const { status } = useSubscription();
  const { data: info, isLoading: devLoading, refetch: refetchInfo } = useUserInfo();
  const navigate = useNavigate();
  const [section, setSection] = useState<Section>('overview');
  const [devBusy, setDevBusy] = useState<string>('');
  const [devErr, setDevErr] = useState<string>('');
  // Sites/rename UI state
  const [renamingId, setRenamingId] = useState<string>('');   // device_id being renamed
  const [renameVal, setRenameVal] = useState<string>('');
  // Device whose password is being reset (the panel under its row).
  const [resettingId, setResettingId] = useState<string>('');
  const [creatingSite, setCreatingSite] = useState<boolean>(false);
  const [siteNameVal, setSiteNameVal] = useState<string>('');
  const [editingSiteId, setEditingSiteId] = useState<string>('');
  const [editingSiteVal, setEditingSiteVal] = useState<string>('');

  useEffect(() => {
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);

  // Small helper: surface the server's error detail (string or {message}).
  const errText = (e: any, fallback: string) => {
    const msg = e?.response?.data?.detail;
    return typeof msg === 'string' ? msg : (msg?.message || fallback);
  };

  // Pause / resume / remove a linked device. Pause and remove kill the device's
  // session tokens on the server, so it's forced to re-authenticate and gets
  // blocked at next launch (paused = temporary, removed = permanent tombstone).
  const deviceAction = async (deviceId: string, kind: 'pause' | 'resume' | 'remove') => {
    if (kind === 'remove' &&
      !window.confirm('Remove this device from your account? It will be signed out and blocked from signing back in. This frees up a device slot.')) {
      return;
    }
    setDevErr('');
    setDevBusy(deviceId + ':' + kind);
    try {
      if (kind === 'remove') {
        await api.delete(`/api/devices/${encodeURIComponent(deviceId)}`);
      } else {
        await api.post(`/api/devices/${encodeURIComponent(deviceId)}/access`, {
          action: kind === 'pause' ? 'pause' : 'resume',
        });
      }
      await refetchInfo();
    } catch (e: any) {
      setDevErr(errText(e, 'Could not update that device. Please try again.'));
    } finally {
      setDevBusy('');
    }
  };

  // Assign a device to a Site (siteId null → Unassigned), or mark it as
  // Mobile/Roaming — a phone or laptop that travels and so belongs to no single
  // site. The two are mutually exclusive; the server clears one when you set the
  // other, so this only ever calls one endpoint.
  const moveDevice = async (deviceId: string, target: string | null) => {
    setDevErr('');
    setDevBusy(deviceId + ':move');
    try {
      if (target === ROAMING) {
        await api.post(`/api/devices/${encodeURIComponent(deviceId)}/roaming`, { roaming: true });
      } else {
        await api.post(`/api/devices/${encodeURIComponent(deviceId)}/site`, { site_id: target });
      }
      await refetchInfo();
    } catch (e: any) {
      setDevErr(errText(e, 'Could not move that device.'));
    } finally {
      setDevBusy('');
    }
  };

  // Rename a device (empty → clear back to the device's own hostname).
  const saveRename = async (deviceId: string) => {
    setDevErr('');
    setDevBusy(deviceId + ':rename');
    try {
      await api.patch(`/api/devices/${encodeURIComponent(deviceId)}/name`, { display_name: renameVal.trim() });
      setRenamingId('');
      setRenameVal('');
      await refetchInfo();
    } catch (e: any) {
      setDevErr(errText(e, 'Could not rename that device.'));
    } finally {
      setDevBusy('');
    }
  };

  const createSite = async () => {
    const name = siteNameVal.trim();
    if (!name) { setCreatingSite(false); return; }
    setDevErr('');
    setDevBusy('site:create');
    try {
      await api.post('/api/sites', { name });
      setSiteNameVal('');
      setCreatingSite(false);
      await refetchInfo();
    } catch (e: any) {
      setDevErr(errText(e, 'Could not create that Site.'));
    } finally {
      setDevBusy('');
    }
  };

  const renameSite = async (siteId: string) => {
    const name = editingSiteVal.trim();
    if (!name) { setEditingSiteId(''); return; }
    setDevErr('');
    setDevBusy('site:' + siteId + ':rename');
    try {
      await api.patch(`/api/sites/${encodeURIComponent(siteId)}`, { name });
      setEditingSiteId('');
      setEditingSiteVal('');
      await refetchInfo();
    } catch (e: any) {
      setDevErr(errText(e, 'Could not rename that Site.'));
    } finally {
      setDevBusy('');
    }
  };

  const deleteSite = async (siteId: string, name: string) => {
    if (!window.confirm(`Delete the Site "${name}"? Its devices won't be removed — they'll just move to Unassigned.`)) return;
    setDevErr('');
    setDevBusy('site:' + siteId + ':delete');
    try {
      await api.delete(`/api/sites/${encodeURIComponent(siteId)}`);
      await refetchInfo();
    } catch (e: any) {
      setDevErr(errText(e, 'Could not delete that Site.'));
    } finally {
      setDevBusy('');
    }
  };

  if (loading) {
    return (
      <div className="auth" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  if (!user) return null;

  const devices = info?.devices ?? [];
  const isPro = status?.is_pro ?? (status?.status === 'active' || status?.status === 'trialing');
  const limit = status?.device_limit ?? 2;
  const used = status?.devices_used ?? devices.length;
  const pct = isPro ? 100 : Math.min(100, limit > 0 ? Math.round((used / limit) * 100) : 0);
  const firstName = user.name ? user.name.split(' ')[0] : null;
  const memberSince = fmtDate(user.created_at);

  const heads: Record<Section, { title: string; sub: string }> = {
    overview: { title: `Welcome back${firstName ? `, ${firstName}` : ''}`, sub: 'Your account at a glance.' },
    devices: { title: 'Devices', sub: 'Every device signed in to this account.' },
    downloads: { title: 'Downloads', sub: 'Install Local Area Cloud, then sign in to link a device.' },
    plan: { title: 'Plan & billing', sub: 'What you have and how to upgrade.' },
    account: { title: 'Account', sub: 'Your profile details.' },
  };

  return (
    <div className="auth">
      <Seo title="Dashboard — Local Area Cloud" description="Manage your Local Area Cloud account, devices, and subscription." path="/dashboard" noindex />
      <div className="dash">
        {/* ---- sidebar ---- */}
        <aside className="dash-side">
          <div className="dash-side-id">
            <div className="dash-side-name">{user.name || 'Your account'}</div>
            <div className="dash-side-email">{user.email}</div>
          </div>
          <nav className="dash-nav">
            {NAV.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                className={`dash-navitem${section === key ? ' active' : ''}`}
                onClick={() => setSection(key)}
                aria-current={section === key ? 'page' : undefined}
              >
                <Icon />
                {label}
              </button>
            ))}
          </nav>
        </aside>

        {/* ---- content ---- */}
        <main className="dash-main">
          <div className="dash-head">
            <h1>{heads[section].title}</h1>
            <p>{heads[section].sub}</p>
          </div>

          {section === 'overview' && (
            <>
              <div className="dash-grid">
                <div className="dash-stat">
                  <div className="k">Plan <span className={`auth-badge${isPro ? ' ok' : ''}`}>{isPro ? 'Pro' : 'Free'}</span></div>
                  <div className="v">{isPro ? 'Pro' : 'Free'}</div>
                  <div className="sub">{isPro ? 'Unlimited devices + VPN' : 'All features · up to 2 devices'}</div>
                </div>
                <div className="dash-stat">
                  <div className="k">Devices</div>
                  <div className="v">{isPro ? used : `${used} / ${limit}`}</div>
                  {!isPro && (
                    <div className="dash-bar" aria-hidden><span style={{ width: `${pct}%` }} /></div>
                  )}
                  <div className="sub">{isPro ? 'connected' : `${Math.max(0, limit - used)} slot${limit - used === 1 ? '' : 's'} remaining`}</div>
                </div>
                <div className="dash-stat">
                  <div className="k">Member since</div>
                  <div className="v" style={{ fontSize: 20 }}>{memberSince || '—'}</div>
                  <div className="sub">{user.email}</div>
                </div>
              </div>

              <div className="auth-panel">
                <h2>Get started</h2>
                <ul className="dash-features">
                  <li><ICheck /> Install Local Area Cloud on your computers and phones.</li>
                  <li><ICheck /> Sign in on each device to link it to this account (up to {limit} on Free).</li>
                  <li><ICheck /> Share files device-to-device over your local network — no cloud in the middle.</li>
                </ul>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 18 }}>
                  <button type="button" className="auth-btn" style={{ width: 'auto', padding: '0 20px' }} onClick={() => setSection('downloads')}>
                    Download the apps
                  </button>
                  {!isPro && (
                    <button type="button" className="auth-btn auth-btn-ghost" style={{ width: 'auto', padding: '0 20px' }} onClick={() => navigate('/payment-required')}>
                      Upgrade to Pro
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {section === 'devices' && (() => {
            const sites: Site[] = info?.sites ?? [];
            const anyBusy = !!devBusy;
            const bySite = new Map<string, DashDevice[]>();
            const roaming: DashDevice[] = [];
            const unassigned: DashDevice[] = [];
            devices.forEach((d) => {
              // Roaming wins over site_id: the server keeps them exclusive, but
              // an older row could still carry both.
              if (isRoaming(d)) {
                roaming.push(d);
              } else if (d.site_id) {
                if (!bySite.has(d.site_id)) bySite.set(d.site_id, []);
                bySite.get(d.site_id)!.push(d);
              } else {
                unassigned.push(d);
              }
            });

            const renderDevice = (d: DashDevice) => {
              const Icon = platformIcon(d.platform);
              const paused = d.status === 'paused';
              const busyPause = devBusy === d.device_id + ':pause' || devBusy === d.device_id + ':resume';
              const busyRemove = devBusy === d.device_id + ':remove';
              const isRenaming = renamingId === d.device_id;
              const shownName = d.display_name || d.device_name || 'Unnamed device';
              return (
                <React.Fragment key={d.device_id}>
                <div className="dev-item">
                  <span className="dev-ico"><Icon /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {isRenaming ? (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                          className="auth-input" autoFocus value={renameVal} maxLength={40}
                          placeholder={d.device_name || 'Device name'}
                          onChange={(e) => setRenameVal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveRename(d.device_id);
                            if (e.key === 'Escape') { setRenamingId(''); setRenameVal(''); }
                          }}
                          style={{ maxWidth: 220, height: 34, padding: '0 10px' }}
                        />
                        <button type="button" className="dev-btn" disabled={anyBusy} onClick={() => saveRename(d.device_id)}>Save</button>
                        <button type="button" className="dev-btn" disabled={anyBusy} onClick={() => { setRenamingId(''); setRenameVal(''); }}>Cancel</button>
                      </div>
                    ) : (
                      <div className="dev-name">
                        {shownName}
                        {paused && <span className="auth-badge" style={{ marginLeft: 8 }}>Paused</span>}
                        <button
                          type="button" className="linklike" style={{ marginLeft: 10, fontSize: 12 }} disabled={anyBusy}
                          onClick={() => { setRenamingId(d.device_id); setRenameVal(d.display_name || ''); }}
                        >Rename</button>
                      </div>
                    )}
                    <div className="dev-meta">
                      {d.platform || 'Unknown platform'}
                      {d.ip_address && d.ip_address !== '0.0.0.0' ? ` · ${d.ip_address}` : ''}
                      {d.vpn_ip ? ` · VPN ${d.vpn_ip}` : ''}
                      {' · last seen '}{fmtWhen(d.last_seen)}
                      {paused && ' · access paused'}
                    </div>
                  </div>
                  <div className="dev-actions">
                    <select
                      className="dev-site-select"
                      value={isRoaming(d) ? ROAMING : d.site_id || ''}
                      disabled={anyBusy}
                      onChange={(e) => moveDevice(d.device_id, e.target.value || null)}
                      aria-label="Move device to a Site"
                    >
                      <option value="">Unassigned</option>
                      {sites.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
                      <option value={ROAMING}>Mobile/Roaming</option>
                    </select>
                    <button
                      type="button" className="dev-btn" disabled={anyBusy}
                      onClick={() => deviceAction(d.device_id, paused ? 'resume' : 'pause')}
                    >{busyPause ? '…' : paused ? 'Resume' : 'Pause'}</button>
                    <button
                      type="button" className="dev-btn" disabled={anyBusy || resettingId === d.device_id}
                      onClick={() => setResettingId(d.device_id)}
                      title="Clear this device's password. You'll need to see its screen to confirm."
                    >Reset password</button>
                    <button
                      type="button" className="dev-btn danger" disabled={anyBusy}
                      onClick={() => deviceAction(d.device_id, 'remove')}
                    >{busyRemove ? '…' : 'Remove'}</button>
                  </div>
                </div>
                {resettingId === d.device_id && (
                  <DeviceResetPanel
                    deviceId={d.device_id}
                    name={shownName}
                    platform={d.platform}
                    onClose={() => setResettingId('')}
                  />
                )}
                </React.Fragment>
              );
            };

            return (
              <div className="auth-panel" style={{ marginTop: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h2 style={{ marginBottom: 0 }}>Devices</h2>
                    <span className="auth-badge">{isPro ? `${used} connected` : `${used} of ${limit}`}</span>
                  </div>
                  {creatingSite ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        className="auth-input" autoFocus value={siteNameVal} maxLength={40} placeholder="Site name (e.g. Home)"
                        onChange={(e) => setSiteNameVal(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') createSite();
                          if (e.key === 'Escape') { setCreatingSite(false); setSiteNameVal(''); }
                        }}
                        style={{ maxWidth: 200, height: 34, padding: '0 10px' }}
                      />
                      <button type="button" className="dev-btn" disabled={anyBusy} onClick={createSite}>Add</button>
                      <button type="button" className="dev-btn" disabled={anyBusy} onClick={() => { setCreatingSite(false); setSiteNameVal(''); }}>Cancel</button>
                    </div>
                  ) : (
                    <button
                      type="button" className="auth-btn auth-btn-ghost" style={{ width: 'auto', padding: '0 16px', height: 36 }}
                      onClick={() => setCreatingSite(true)}
                    >+ New Site</button>
                  )}
                </div>

                {devLoading ? (
                  <div style={{ padding: 24 }}><LoadingSpinner /></div>
                ) : devices.length === 0 ? (
                  <div className="dash-empty">
                    No devices linked yet.<br />
                    Download the app and sign in to link your first device.
                    <div style={{ marginTop: 16 }}>
                      <button type="button" className="auth-btn" style={{ width: 'auto', padding: '0 20px', margin: '0 auto' }} onClick={() => setSection('downloads')}>
                        Go to downloads
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {sites.map((st) => {
                      const list = bySite.get(st.id) || [];
                      const isEditing = editingSiteId === st.id;
                      return (
                        <div key={st.id} className="dev-site">
                          <div className="dev-site-head">
                            {isEditing ? (
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <input
                                  className="auth-input" autoFocus value={editingSiteVal} maxLength={40}
                                  onChange={(e) => setEditingSiteVal(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') renameSite(st.id);
                                    if (e.key === 'Escape') { setEditingSiteId(''); setEditingSiteVal(''); }
                                  }}
                                  style={{ maxWidth: 200, height: 32, padding: '0 10px' }}
                                />
                                <button type="button" className="dev-btn" disabled={anyBusy} onClick={() => renameSite(st.id)}>Save</button>
                                <button type="button" className="dev-btn" disabled={anyBusy} onClick={() => { setEditingSiteId(''); setEditingSiteVal(''); }}>Cancel</button>
                              </div>
                            ) : (
                              <>
                                <span className="dev-site-name"><ISite />{st.name}</span>
                                <span className="auth-badge">{list.length}</span>
                                <span style={{ flex: 1 }} />
                                <button type="button" className="linklike" style={{ fontSize: 12.5 }} disabled={anyBusy} onClick={() => { setEditingSiteId(st.id); setEditingSiteVal(st.name); }}>Rename</button>
                                <button type="button" className="dev-btn danger" style={{ padding: '5px 11px' }} disabled={anyBusy} onClick={() => deleteSite(st.id, st.name)}>Delete</button>
                              </>
                            )}
                          </div>
                          {list.length === 0 ? (
                            <div className="dev-site-empty">No devices here yet — use a device's dropdown to move it in.</div>
                          ) : (
                            <div className="dev-group">{list.map(renderDevice)}</div>
                          )}
                        </div>
                      );
                    })}

                    {/* Devices that travel. They belong to no Site by design, so
                        they get their own group rather than sitting in
                        Unassigned looking like an oversight. */}
                    {roaming.length > 0 && (
                      <div className="dev-site">
                        <div className="dev-site-head">
                          <span className="dev-site-name"><IRoam />Mobile/Roaming</span>
                          <span className="auth-badge">{roaming.length}</span>
                        </div>
                        <div className="dev-group">{roaming.map(renderDevice)}</div>
                      </div>
                    )}

                    <div className="dev-site">
                      <div className="dev-site-head">
                        <span className="dev-site-name" style={{ color: 'var(--mute)' }}><ISite />Unassigned</span>
                        <span className="auth-badge">{unassigned.length}</span>
                      </div>
                      {unassigned.length === 0 ? (
                        <div className="dev-site-empty">Every device is grouped into a Site.</div>
                      ) : (
                        <div className="dev-group">{unassigned.map(renderDevice)}</div>
                      )}
                    </div>
                  </div>
                )}

                {devErr && (
                  <p className="auth-note" style={{ textAlign: 'left', marginTop: 14, color: 'var(--err, #e26d6d)' }}>{devErr}</p>
                )}
                <p className="auth-note" style={{ textAlign: 'left', marginTop: 16 }}>
                  <strong>Sites</strong> group your devices by location (Home, Office…) — it's just for organization here and doesn't change how devices connect.
                  {' '}<strong>Rename</strong> sets a friendly name; <strong>Pause</strong> temporarily blocks a device; <strong>Reset password</strong> clears a forgotten device password once you confirm a code shown on that device; <strong>Remove</strong> unlinks it and frees a slot.
                </p>
                {!isPro && used >= limit && (
                  <p className="auth-note" style={{ textAlign: 'left', marginTop: 12, color: 'var(--champ)' }}>
                    You've reached your {limit}-device limit. Upgrade to Pro for unlimited devices.
                  </p>
                )}
              </div>
            );
          })()}

          {section === 'downloads' && (
            <div className="dl-grid">
              <DownloadCard
                icon={IWin} name="Windows" note="Windows 10 & 11 · 64-bit"
                recommended={/Win/i.test(navigator.userAgent)}
                links={[{ href: DL.win, label: 'Download (.exe)' }]}
              />
              <DownloadCard
                icon={IApple} name="macOS" note="Universal · Intel & Apple Silicon"
                recommended={/Mac/i.test(navigator.userAgent)}
                links={[{ href: DL.mac, label: 'Download (.zip)' }]}
              />
              <DownloadCard
                icon={ILinux} name="Linux" note="Debian/Ubuntu installer · AppImage for other distros"
                recommended={/Linux/i.test(navigator.userAgent) && !/Android/i.test(navigator.userAgent)}
                links={[
                  { href: DL.linuxDeb, label: 'Debian (.deb)', primary: true },
                  { href: DL.linuxAppImage, label: 'AppImage' },
                ]}
              />
              <DownloadCard
                icon={IMobile} name="iOS & Android" note="On the App Store & Google Play"
                links={[
                  { href: DL.appStore, label: 'App Store', primary: true },
                  { href: DL.googlePlay, label: 'Google Play' },
                ]}
              />
            </div>
          )}

          {section === 'plan' && (
            <>
              <div className="auth-panel" style={{ marginTop: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <h2 style={{ marginBottom: 0 }}>Your plan</h2>
                  <span className={`auth-badge${isPro ? ' ok' : ''}`}>{isPro ? 'Pro' : 'Free'}</span>
                </div>
                <div className="auth-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                  <span className="auth-val" style={{ fontSize: 26 }}>{isPro ? 'Pro' : 'Free'}</span>
                  <span className="auth-key">{isPro ? 'Unlimited devices, VPN, and every feature.' : 'All features included, on up to 2 devices.'}</span>
                </div>
                <div className="auth-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span className="auth-key">Devices</span>
                    <span className="auth-val" style={{ fontSize: 15 }}>{isPro ? `${used} connected` : `${used} of ${limit} used`}</span>
                  </div>
                  <div className="dash-bar" aria-hidden><span style={{ width: `${pct}%`, background: !isPro && used >= limit ? 'var(--champ)' : 'var(--teal-bright)' }} /></div>
                </div>
                {isPro && status?.current_period_end && (
                  <div className="auth-row">
                    <span className="auth-key">Renews / ends</span>
                    <span className="auth-val">{fmtDate(status.current_period_end)}</span>
                  </div>
                )}
              </div>

              {!isPro && (
                <div className="auth-panel">
                  <h2>Why Pro</h2>
                  <ul className="dash-features">
                    <li><ICheck /> Unlimited devices</li>
                    <li><ICheck /> Secure VPN for device-to-device access anywhere</li>
                    <li><ICheck /> Priority support</li>
                  </ul>
                </div>
              )}

              {/* Inline plan/cadence picker. Existing Stripe subscribers change
                  their plan IN PLACE (mode="change" — no new trial/duplicate);
                  Free users and manual-grant accounts start a checkout. */}
              <PlanPicker
                mode={status?.has_subscription ? 'change' : 'checkout'}
                heading={
                  status?.has_subscription ? 'Change your plan'
                    : isPro ? 'Start a paid subscription'
                    : 'Upgrade to Pro'
                }
                note={status?.has_subscription
                  ? 'Switch your billing cadence below. The change applies to your current subscription (prorated by Stripe) — no new trial, no duplicate charge.'
                  : 'Pay monthly for flexibility, or save up to 44% per month with the yearly plan. First-time subscribers get a 14-day free trial.'}
              />

              <div className="auth-panel">
                <h2>Billing</h2>
                <div className="auth-row">
                  <span className="auth-key">Current charge</span>
                  <span className="auth-val">{isPro ? '$10.00 / year' : '$0.00 — Free plan'}</span>
                </div>
                <div className="auth-row">
                  <span className="auth-key">Billing history</span>
                  <span className="auth-key">No charges yet</span>
                </div>
              </div>
            </>
          )}

          {section === 'account' && (
            <div style={{ display: 'grid', gap: 18 }}>
              <div className="auth-panel" style={{ marginTop: 0 }}>
                <h2>Profile</h2>
                <div className="auth-row"><span className="auth-key">Email</span><span className="auth-val">{user.email}</span></div>
                {user.name && <div className="auth-row"><span className="auth-key">Name</span><span className="auth-val">{user.name}</span></div>}
                {user.phone && <div className="auth-row"><span className="auth-key">Phone</span><span className="auth-val">{user.phone}</span></div>}
                {memberSince && <div className="auth-row"><span className="auth-key">Member since</span><span className="auth-val">{memberSince}</span></div>}
                <div className="auth-row"><span className="auth-key">Plan</span><span className="auth-val">{isPro ? 'Pro' : 'Free'}</span></div>
              </div>
              {info && <PasswordSettings email={user.email} hasPassword={info.has_password ?? true} onChanged={() => refetchInfo()} />}
              <TwoFactorSettings />
              <DiscordSettings />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

/* ---------- download card ---------- */
const DownloadCard: React.FC<{
  icon: React.FC;
  name: string;
  note: string;
  recommended?: boolean;
  links: { href: string; label: string; primary?: boolean }[];
}> = ({ icon: Icon, name, note, recommended, links }) => (
  <div className={`dl-card${recommended ? ' rec' : ''}`}>
    <span className="dl-ico"><Icon /></span>
    <div className="dl-tagrow">{recommended && <span className="auth-badge ok">Your device</span>}</div>
    <h3>{name}</h3>
    <p className="dl-note">{note}</p>
    <div className="dl-links">
      {links.length === 0 ? (
        <button type="button" disabled className="auth-btn auth-btn-ghost">Coming soon</button>
      ) : (
        (() => {
          const hasExplicitPrimary = links.some((l) => l.primary);
          return links.map((l, i) => {
            const isPrimary = l.primary ?? (!hasExplicitPrimary && i === 0);
            return (
              <a
                key={l.href}
                href={l.href}
                className={`auth-btn${isPrimary ? '' : ' auth-btn-ghost'}`}
                style={{ textDecoration: 'none' }}
              >
                {l.label}
              </a>
            );
          });
        })()
      )}
    </div>
  </div>
);
